import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import lib from '../../assets/utils/lib';
import './org.css';
import './orgs-dashboard.css';
import DashboardHero from '../dashboard/components/DashboardHero';
import DashboardSection from '../dashboard/components/DashboardSection';
import { TableSkeleton } from '../dashboard/components/DashboardSkeleton';
import { hasMetricValue } from '../dashboard/utils';
import OrganizationListingCard from './OrganizationListingCard';

import { Table, Modal, Input, Button, Select, Typography, notification, Col, Row, Divider, Empty, Statistic, Card, Tooltip, Segmented, Tag, Spin, Alert, Dropdown, Grid, Pagination, Space, Badge } from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, SettingOutlined, CheckCircleOutlined, CloseCircleOutlined, AppstoreOutlined, DatabaseOutlined, SearchOutlined, EnvironmentOutlined, TeamOutlined, CalendarOutlined, MailOutlined, MoreOutlined, LockOutlined, TableOutlined, BankOutlined, WarningOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { keysHaveEmptyValues } from "../../assets/utils/Helpers";
import CountUp from 'react-countup';
import { RenewOrgPolicy } from "../compoinents/policyRenew";
import { states } from "../../assets/utils/config";
import PasswordManagementModal from "./components/PasswordManagementModal";
import { resolveOrgActionAccess } from "./orgActionPermissions";
import OrganizationsDirectoryGuide from "./components/OrganizationsDirectoryGuide";
import DismissibleAlert from "../../components/DismissibleAlert";
import usePendingStaffUploadsCount from "./hooks/usePendingStaffUploadsCount";
import useEnrollmentConflictsCount from "./hooks/useEnrollmentConflictsCount";
import { canManageStaffUploads } from "./planRequestHelpers";
import { canManageEnrollmentConflicts } from "./enrollmentConflictHelpers";
import { canViewBulkUploadLogs } from "../../components/CarePrograms/careProgramPermissions";

const { Option } = Select;
const { Text } = Typography;

const formatter = (value) => <CountUp end={value} separator="," />;

export const ORGS_WORK_QUEUES_ALERT_BANNER_KEY = 'orgs-work-queues-alert-v1';

function Organizations(props) {
  const { user } = useAuth();
  const { hasAnyPermission, canStayOnModule } = usePermissions();
  const extended = props.extended !== undefined ? props.extended : true;
  const timerId = useRef();
  const skipFilterEffect = useRef(true);
  const [users, setUsers] = useState([]);
  const [orgFilters, setOrgFilters] = useState({
    search: '',
    state: '',
    status: '',
    enrollment_segment: '',
    include_self_pay: 1,
  });
  const [orgPagination, setOrgPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedView = searchParams.get('view');

  // Legacy ?view= modes → dedicated work-queue routes (directory stays companies-only).
  useEffect(() => {
    if (requestedView === 'plan-requests') {
      const params = new URLSearchParams({ tab: 'plan-requests' });
      const orgId = searchParams.get('org_id');
      const orgName = searchParams.get('org_name');
      if (orgId) params.set('org_id', orgId);
      if (orgName) params.set('org_name', orgName);
      navigate(`/plans?${params.toString()}`, { replace: true });
      return;
    }
    if (requestedView === 'staff-uploads') {
      const params = new URLSearchParams();
      ['org_id', 'org_name', 'org_social_no'].forEach((key) => {
        const value = searchParams.get(key);
        if (value) params.set(key, value);
      });
      const qs = params.toString();
      navigate(qs ? `/corporations/staff-uploads?${qs}` : '/corporations/staff-uploads', { replace: true });
      return;
    }
    if (requestedView === 'enrollment-conflicts') {
      const params = new URLSearchParams();
      ['session_id', 'social_no', 'organization_id', 'status'].forEach((key) => {
        const value = searchParams.get(key);
        if (value) params.set(key, value);
      });
      const qs = params.toString();
      navigate(qs ? `/corporations/enrollment-holds?${qs}` : '/corporations/enrollment-holds', { replace: true });
    }
  }, [requestedView, searchParams, navigate]);

  const canReviewStaffUploads = useMemo(
    () => canManageStaffUploads(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const canReviewEnrollmentConflicts = useMemo(
    () => canManageEnrollmentConflicts(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const canSeeUploadHistory = useMemo(
    () => canViewBulkUploadLogs(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  // Row actions mirror the endpoint behind each one. The directory used to
  // render Edit, Manage plans, Renew, Disable and Set password on every card
  // for anyone who could open the screen.
  const orgActions = useMemo(
    () => resolveOrgActionAccess(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  const { count: pendingStaffUploadsCount } = usePendingStaffUploadsCount(
    Boolean(user && canReviewStaffUploads),
  );

  const {
    open: openEnrollmentConflicts,
    pausedSessions: pausedEnrollmentSessions,
  } = useEnrollmentConflictsCount(Boolean(user && canReviewEnrollmentConflicts));

  const showWorkQueues = canReviewStaffUploads || canReviewEnrollmentConflicts || canSeeUploadHistory;
  const hasQueueAttention = (canReviewStaffUploads && pendingStaffUploadsCount > 0)
    || (canReviewEnrollmentConflicts && openEnrollmentConflicts > 0);

  useEffect(() => {
    const idToClear = timerId.current; // capture for cleanup - ref may change before cleanup runs
    if (!user) return undefined;
    if (!canStayOnModule('organizations', [1, 5])) {
      navigate('/dashboard');
      return () => clearInterval(idToClear);
    }
    clearInterval(idToClear);
    loadOrganization(1, 20);
    return () => clearInterval(idToClear);
  }, [user, canStayOnModule]) // eslint-disable-line react-hooks/exhaustive-deps -- idToClear captured at start of effect

  // Handle filter changes with debouncing (skip initial mount — user effect loads first)
  useEffect(() => {
    if (!user || !canStayOnModule('organizations', [1, 5])) return;
    if (skipFilterEffect.current) {
      skipFilterEffect.current = false;
      return;
    }
    setOrgPagination(prev => ({ ...prev, current: 1 }));
    const timeoutId = setTimeout(() => {
      loadOrganization(1, orgPagination.pageSize, true);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [orgFilters]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (value) => {
    setOrgFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key, value) => {
    setOrgFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setOrgFilters({
      search: '',
      state: '',
      status: '',
      enrollment_segment: '',
      include_self_pay: 1,
    });
  };

  const [visibleCreate, setVisibleCreate] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const fieldsCreate = ['org_name', 'org_email', 'org_address', 'org_state', 'org_city', 'org_social_no']

  const [formCreate, setFormCreate] = useState({});

  const [view, setView] = useState('list');

  const loadOrganization = async (page = 1, limit = 20, showSearchLoading = false) => {
    if (showSearchLoading) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const params = {
        page,
        limit,
        ...(orgFilters.search && { search: orgFilters.search }),
        ...(orgFilters.state && { state: orgFilters.state }),
        ...(orgFilters.status !== '' && { status: orgFilters.status }),
        ...(orgFilters.enrollment_segment && { enrollment_segment: orgFilters.enrollment_segment }),
        include_self_pay: orgFilters.include_self_pay ? 1 : 0,
      };

      const resp = await lib.getOrganizationsV2(params);
      const body = resp?.data;
      
      if (body && Number(body.error) === 0) {
        setUsers(Array.isArray(body.data) ? body.data : []);
        setOrgPagination(prev => ({
          ...prev,
          current: body.pagination?.page || page,
          pageSize: body.pagination?.limit || limit,
          total: body.pagination?.total || 0,
        }));

        // Show success message for search results
        if (orgFilters.search && body.data?.length > 0) {
          notification.success({
            message: `Found ${body.data.length} organization(s)`,
            description: `Search results for "${orgFilters.search}"`,
            placement: 'topRight',
            duration: 2
          });
        } else if (orgFilters.search && body.data?.length === 0) {
          notification.info({
            message: 'No organizations found',
            description: `No results for "${orgFilters.search}"`,
            placement: 'topRight',
            duration: 3
          });
        }
      } else {
        const errorMsg = body?.message || 'Failed to load organizations';
        setError(errorMsg);
        notification.error({ message: errorMsg });
        setUsers([]);
        setOrgPagination(prev => ({ ...prev, current: page, pageSize: limit, total: 0 }));
      }
    } catch (e) {
      const errorMsg = e?.message || 'Network error while loading organizations';
      setError(errorMsg);
      notification.error({ message: errorMsg });
      setUsers([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const openOrg = useCallback((record) => {
    navigate(`/corporations/users/${record.hash}`, { state: record });
  }, [navigate]);

  const refreshCurrent = useCallback(() => {
    loadOrganization(orgPagination.current, orgPagination.pageSize);
  }, [orgPagination.current, orgPagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasActiveFilters = Boolean(
    orgFilters.search
    || orgFilters.state
    || orgFilters.status !== ''
    || orgFilters.enrollment_segment,
  );

  const isOrgActive = (record) => parseInt(record?.org_status, 10) === 1;

  const activeOnPage = useMemo(
    () => users.filter(isOrgActive).length,
    [users]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(orgPagination.total / orgPagination.pageSize)),
    [orgPagination.total, orgPagination.pageSize]
  );

  const heroMetrics = useMemo(() => [
    {
      key: 'total',
      title: 'Total organizations',
      value: orgPagination.total,
      color: '#3497D3',
      icon: <BankOutlined />,
      subtitle: hasActiveFilters ? 'Matching filters' : 'All organizations',
    },
    {
      key: 'page',
      title: 'On this page',
      value: users.length,
      color: '#722ed1',
      icon: <UnorderedListOutlined />,
      subtitle: `Page ${orgPagination.current} of ${totalPages}`,
    },
    {
      key: 'active',
      title: 'Active',
      value: activeOnPage,
      color: '#49B870',
      icon: <CheckCircleOutlined />,
      subtitle: 'On this page',
    },
    {
      key: 'policies',
      title: 'Active policies',
      value: users.reduce((sum, row) => sum + (Number(row?.active_policies) || 0), 0),
      color: '#3497D3',
      icon: <TeamOutlined />,
      subtitle: 'On this page',
    },
  ].filter((m) => hasMetricValue(m.value) || m.key === 'page'), [
    orgPagination,
    totalPages,
    users,
    activeOnPage,
    hasActiveFilters,
  ]);

  const handleAdd = async () => {
    console.log(formCreate)
    if (keysHaveEmptyValues(formCreate, fieldsCreate)) {
      notification.error({
        message: "Please fill all the fields", placement: 'topLeft'
      })
      return;
    }
    try {
      const response = await lib.createOrg(formCreate);
      const data = response?.data;

      if (Number(data?.error) === 0) {
        notification.success({ message: data?.data?.message || data?.message, placement: 'topLeft' });
        loadOrganization(orgPagination.current, orgPagination.pageSize);
        handleCancel();
      } else {
        notification.error({
          message: data?.message || 'Unable to create organization',
          description: data?.data?.message || data?.errors?.join?.(', ') || null,
          placement: 'topLeft',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Organization Creation Failed',
        description: error?.response?.data?.message || error?.message || 'An unexpected error occurred',
        placement: 'topLeft',
      });
    }
  };

  const handleCancel = () => {
    setVisibleCreate(false);
    setFormCreate({});
  };

  const renderOrgNameCell = (text, record) => {
    const pendingUploads = Number(record?.pending_staff_uploads_count) || 0;
    return (
      <span className="orgs-name-cell">
        <Button type="link" className="orgs-name-link" onClick={() => openOrg(record)}>
          {text}
        </Button>
        {canReviewStaffUploads && pendingUploads > 0 ? (
          <Tooltip title={`${pendingUploads} staff upload${pendingUploads === 1 ? '' : 's'} awaiting review`}>
            <Tag
              color="gold"
              className="orgs-staff-upload-badge"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/corporations/users/${record.hash}?tab=members&section=uploads`, { state: record });
              }}
            >
              {pendingUploads} pending
            </Tag>
          </Tooltip>
        ) : null}
      </span>
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'org_name',
      key: 'org_name',
      render: renderOrgNameCell,
    },
    {
      title: 'Org ID',
      dataIndex: 'org_id',
      key: 'org_id',
      render: (__, record) => record?.org_id ?? record?.hash ?? '—',
    },
    {
      title: 'Org code',
      dataIndex: 'org_social_no',
      key: 'org_social_no',
    },
    {
      title: 'Renewal Date',
      dataIndex: 'org_renew_date',
      key: 'org_renew_date',
      render: (__, record) => __?.split(' ')[0]
    },
    {
      title: 'Expiry Date',
      dataIndex: 'org_expiry_date',
      key: 'org_expiry_date',
      render: (__, record) => __?.split(' ')[0]
    },
    {
      title: 'Status',
      dataIndex: 'org_status',
      key: 'org_status',
      render: (__, record) => (parseInt(record.org_status) ? 'Enabled' : 'Disabled')
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 120,
      render: (__, record) => (
        <Button type="link" size="small" onClick={() => openOrg(record)}>
          Open
        </Button>
      ),
    },
  ];

  const columnsMinimized = [
    {
      title: 'Name',
      dataIndex: 'org_name',
      key: 'org_name',
      render: renderOrgNameCell,
    },
    {
      title: 'Org ID',
      dataIndex: 'org_id',
      key: 'org_id',
      render: (__, record) => record?.org_id ?? record?.hash ?? '—',
    },
    {
      title: '# Active Policy',
      dataIndex: 'active_policies',
      key: 'active_policies',
    },
    {
      title: 'Contribution',
      dataIndex: 'orgContributions',
      key: 'orgContributions',
      render: (__, record) => <Statistic prefix='&#8358;' title={<strong></strong>} value={__} precision={2} formatter={formatter} />
    }
  ];

  const emptyDescription = hasActiveFilters
    ? 'No organizations match your filters'
    : 'No organizations found';

  const paginationBlock = orgPagination.total > orgPagination.pageSize ? (
    <div className="orgs-pagination">
      <Pagination
        current={orgPagination.current}
        pageSize={orgPagination.pageSize}
        total={orgPagination.total}
        onChange={(page, pageSize) => loadOrganization(page, pageSize || orgPagination.pageSize)}
        showSizeChanger
        showQuickJumper
        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} organizations`}
        pageSizeOptions={['10', '20', '50', '100']}
      />
    </div>
  ) : null;

  const directoryToolbar = (
    <div className="orgs-toolbar orgs-toolbar--inline">
      <Input
        className="orgs-toolbar__search"
        size="small"
        placeholder="Search organizations…"
        prefix={<SearchOutlined />}
        value={orgFilters.search}
        onChange={(e) => handleSearch(e.target.value)}
        onPressEnter={(e) => handleSearch(e.target.value)}
        allowClear
      />
      <Select
        className="orgs-toolbar__select"
        size="small"
        placeholder="State"
        value={orgFilters.state || undefined}
        onChange={(value) => handleFilterChange('state', value ?? '')}
        allowClear
      >
        {states.map((state) => (
          <Option key={state.value} value={state.value}>{state.label}</Option>
        ))}
      </Select>
      <Select
        className="orgs-toolbar__select"
        size="small"
        placeholder="Status"
        value={orgFilters.status === '' ? undefined : orgFilters.status}
        onChange={(value) => handleFilterChange('status', value ?? '')}
        allowClear
      >
        <Option value="1">Active</Option>
        <Option value="0">Inactive</Option>
      </Select>
      <Select
        className="orgs-toolbar__select"
        size="small"
        placeholder="Segment"
        value={orgFilters.enrollment_segment || undefined}
        onChange={(value) => handleFilterChange('enrollment_segment', value ?? '')}
        allowClear
      >
        <Option value="corporate">Corporate</Option>
        <Option value="sme">SME</Option>
      </Select>
      <Button size="small" onClick={clearFilters} disabled={!hasActiveFilters}>
        Clear
      </Button>
      {extended ? (
        <>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[
              { label: 'List', value: 'list', icon: <AppstoreOutlined /> },
              { label: 'Table', value: 'table', icon: <TableOutlined /> },
            ]}
          />
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setVisibleCreate(true)}>
            Add organization
          </Button>
        </>
      ) : null}
      <Button size="small" icon={<ReloadOutlined />} onClick={refreshCurrent} loading={loading}>
        Refresh
      </Button>
    </div>
  );

  const renderDirectoryBody = () => {
    if (loading && !users.length) {
      return view === 'table' ? (
        <TableSkeleton rows={8} columns={6} />
      ) : (
        <div className="orgs-loading"><Spin size="large" /></div>
      );
    }
    if (!users.length) {
      return (
        <Empty
          description={emptyDescription}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {extended ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setVisibleCreate(true)}>
              Add organization
            </Button>
          ) : null}
        </Empty>
      );
    }
    if (view === 'table' || !extended) {
      return (
        <>
          <div className="orgs-table-wrap">
            <Table
              columns={extended ? columns : columnsMinimized}
              dataSource={users}
              rowKey={(r) => r.hash || r.org_id}
              pagination={false}
            />
          </div>
          {paginationBlock}
        </>
      );
    }
    return (
      <>
        <div className="orgs-listing-stack">
          {users.map((row) => (
            <OrganizationListingCard
              key={row.hash || row.org_id}
              record={row}
              onOpen={openOrg}
              onOpenStaffUploads={canReviewStaffUploads ? (record) => navigate(`/corporations/users/${record.hash}?tab=members&section=uploads`, { state: record }) : undefined}
            />
          ))}
        </div>
        {paginationBlock}
      </>
    );
  };

  return (
    <div className="orgs-dashboard">
      <div className="dashboard-page-stack">
        {extended ? (
          <header className="dash-masthead">
            <div className="dash-masthead__lead">
              <h1 className="dash-masthead__title">Organizations</h1>
              <p className="dash-masthead__meta">
                Open a company to manage members, plans, renewals, and uploads.
                Use the Staff uploads or Enrollment holds header buttons when you need the cross-company queues.
              </p>
            </div>
            {showWorkQueues ? (
              <div className="dash-masthead__actions">
                <Space wrap>
                  {(canReviewStaffUploads || canSeeUploadHistory) ? (
                    <Button
                      icon={<DatabaseOutlined />}
                      onClick={() => navigate('/corporations/staff-uploads')}
                    >
                      Staff uploads
                      {canReviewStaffUploads && pendingStaffUploadsCount > 0 ? (
                        <Badge
                          count={pendingStaffUploadsCount}
                          overflowCount={99}
                          style={{ marginLeft: 8 }}
                        />
                      ) : null}
                    </Button>
                  ) : null}
                  {canReviewEnrollmentConflicts ? (
                    <Button
                      icon={<WarningOutlined />}
                      onClick={() => navigate('/corporations/enrollment-holds')}
                    >
                      Enrollment holds
                      {openEnrollmentConflicts > 0 ? (
                        <Badge
                          count={openEnrollmentConflicts}
                          overflowCount={99}
                          style={{ marginLeft: 8 }}
                        />
                      ) : null}
                    </Button>
                  ) : null}
                </Space>
              </div>
            ) : null}
          </header>
        ) : null}

        {extended && showWorkQueues && hasQueueAttention ? (
          <DismissibleAlert
            bannerKey={ORGS_WORK_QUEUES_ALERT_BANNER_KEY}
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            className="orgs-work-queues-alert"
            style={{ marginBottom: 16 }}
            message="Enrollment work waiting across companies"
            description={(
              <div>
                <p style={{ marginBottom: 10 }}>
                  These are not separate products — they are holds and uploads that need attention
                  before membership work can continue. Prefer opening the company when you know which
                  one; use the header buttons for the cross-company lists.
                </p>
                <Space wrap size="small">
                  {canReviewStaffUploads && pendingStaffUploadsCount > 0 ? (
                    <Button
                      type="primary"
                      size="small"
                      icon={<DatabaseOutlined />}
                      onClick={() => navigate('/corporations/staff-uploads')}
                    >
                      Review staff uploads ({pendingStaffUploadsCount})
                    </Button>
                  ) : null}
                  {canReviewEnrollmentConflicts && openEnrollmentConflicts > 0 ? (
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<WarningOutlined />}
                      onClick={() => navigate('/corporations/enrollment-holds')}
                    >
                      Clear enrollment holds ({openEnrollmentConflicts}
                      {pausedEnrollmentSessions > 0 ? ` · ${pausedEnrollmentSessions} with conflicts` : ''})
                    </Button>
                  ) : null}
                  {(canReviewStaffUploads || canSeeUploadHistory) && pendingStaffUploadsCount === 0 ? (
                    <Button
                      size="small"
                      type="link"
                      onClick={() => navigate('/corporations/staff-uploads')}
                    >
                      Staff upload history
                    </Button>
                  ) : null}
                </Space>
              </div>
            )}
          />
        ) : null}

        {extended ? (
          <>
            <OrganizationsDirectoryGuide />
            <section className="dash-overview dash-overview--compact" aria-label="Organizations summary">
              <DashboardHero metrics={heroMetrics} loading={loading && !users.length && !orgPagination.total} />
            </section>
          </>
        ) : null}

        {error ? (
          <Alert
            className="orgs-error-alert"
            message="Error loading organizations"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        ) : null}

        <DashboardSection
          id="orgs-directory"
          icon={<TeamOutlined />}
          title={extended ? 'Organization directory' : 'Organizations'}
          subtitle={
            searchLoading
              ? 'Searching…'
              : `${users.length} of ${orgPagination.total} · page ${orgPagination.current}`
          }
          collapsible={false}
          variant="content"
          action={directoryToolbar}
        >
          {renderDirectoryBody()}
        </DashboardSection>
      </div>
      <Modal
        title="Add Organization"
        open={visibleCreate}
        onOk={handleAdd}
        onCancel={handleCancel}
        forceRender
      >
        <Row gap={6}>
          <Col span={8}>Name</Col>
          <Col span={16}>
            <Input onChange={(e) => setFormCreate(d => ({ ...d, org_name: e.target.value }))} />
          </Col>
        </Row>
        <Row gap={6}>
          <Col span={8}>Email</Col>
          <Col span={16}>
            <Input onChange={(e) => setFormCreate(d => ({ ...d, org_email: e.target.value }))} />
          </Col>
        </Row>
        <Row gap={6}>
          <Col span={8}>Org code</Col>
          <Col span={16}>
            <Input onChange={(e) => setFormCreate(d => ({ ...d, org_social_no: e.target.value }))} />
          </Col>
        </Row>
        <Row gap={6}>
          <Col span={8}>Address</Col>
          <Col span={16}>
            <Input.TextArea onChange={(e) => setFormCreate(d => ({ ...d, org_address: e.target.value }))} rows={5} />
          </Col>
        </Row>
        <Row gap={6}>
          <Col span={8}>State</Col>
          <Col span={16}>
            <Select options={states} onChange={(e) => setFormCreate(d => ({ ...d, org_state: e }))} style={{ width: '100%' }} />
          </Col>
        </Row>
        <Row gap={6}>
          <Col span={8}>City</Col>
          <Col span={16}>
            <Input onChange={(e) => setFormCreate(d => ({ ...d, org_city: e.target.value }))} />
          </Col>
        </Row>
      </Modal>

    </div>
  );
}

export const HoverCard = ({ data, manageHandle, renewHandler, editHandler, statusHandler, passwordHandler }) => {
  const [enabled, setEnabled] = useState(parseInt(data?.org_status));
  const [visiblePassword, setVisiblePassword] = useState(false);
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const actionsRef = useRef(null);
  const cardRef = useRef(null);
  const renewPolicyRef = useRef(null);

  return (
    <div ref={cardRef} className="ant-card-hoverable" style={{ width: '100%', borderRadius: '12px', overflow: 'hidden', position: 'relative' }} >
      <Card
        className="ant-card-hoverable"
        hoverable
        style={{
          border: '1px solid #3497D3',
          boxShadow: '0 2px 8px rgba(52, 151, 211, 0.1)',
          transition: 'all 0.3s ease',
          background: '#FFFFFF',
        }}
        bodyStyle={{ padding: '20px', background: 'transparent' }}
        onClick={() => navigate(`/corporations/users/${data?.hash}`, { state: data })}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
          e.currentTarget.style.transform = 'translateY(-2px)';
          try {
            if (actionsRef && actionsRef.current) {
              actionsRef.current.style.opacity = '1';
              // keep transform steady to avoid reflow under cursor
            }
          } catch { }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
          e.currentTarget.style.transform = 'translateY(0)';
          try {
            if (actionsRef && actionsRef.current) {
              // If moving into actions container, do not hide
              const next = e.relatedTarget;
              if (next && actionsRef.current.contains(next)) return;
              actionsRef.current.style.opacity = '0';
            }
          } catch { }
        }}
      >
        {/* subtle gradient top strip */}
        <div style={{
          position: 'absolute',
          insetInline: 0,
          top: 0,
          height: 3,
          background: '#3497D3'
        }} />
        <div className="status-button" style={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
          <Tooltip title={enabled ? "Active Organization" : "Inactive Organization"}>
            <Badge
              status={enabled ? "success" : "error"}
              text=""
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                boxShadow: `0 0 0 2px ${enabled ? '#49B870' : '#3497D3'}20`
              }}
            />
          </Tooltip>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ position: 'relative' }}>
              {/* soft radial accent */}
              <div style={{
                position: 'absolute',
                inset: -6,
                borderRadius: '50%',
                background: 'radial-gradient(60% 60% at 50% 50%, rgba(52,151,211,0.25) 0%, rgba(255,255,255,0) 70%)',
                filter: 'blur(6px)'
              }} />
              <div style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: '#3497D3',
                border: '1px solid #3497D3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                color: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(52, 151, 211, 0.15)'
              }}>
                {(data?.org_name || 'ORG').split(' ').slice(0, 2).map(s => s?.[0]).join('').toUpperCase()}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Text style={{ fontSize: 16, fontWeight: 700, color: '#3497D3' }}>{data?.org_name}</Text>

              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {data?.org_social_no}
                </Text>
                <Text type="secondary" style={{
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  color: enabled ? '#FFFFFF' : '#3497D3',
                  background: enabled ? '#49B870' : '#FFFFFF',
                  border: `1px solid ${enabled ? '#49B870' : '#3497D3'}`
                }}>{enabled ? 'Active' : 'Inactive'}</Text>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MailOutlined style={{ color: '#3497D3' }} />
              <Text style={{ fontSize: 12 }}>{data?.org_email || 'No email'}</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <EnvironmentOutlined style={{ color: '#49B870' }} />
              <Text style={{ fontSize: 12 }}>{(data?.org_state || 'None')}{data?.org_city ? ` / ${data.org_city}` : ''}</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CalendarOutlined style={{ color: '#3497D3' }} />
              <Text style={{ fontSize: 12 }}>{(data?.org_joined_date && data?.org_joined_date?.startsWith('0')) ? 'Joined: Not Set' : `Joined: ${(new Date(data?.org_joined_date)).toDateString()}`}</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CalendarOutlined style={{ color: '#49B870' }} />
              <Text style={{ fontSize: 12 }}>{(data?.org_expiry_date && data?.org_expiry_date?.startsWith('0')) ? 'Next: Not Set' : `Next: ${(new Date(data?.org_expiry_date)).toDateString()}`}</Text>
            </div>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            {/* Policies Container */}
            <div style={{
              padding: '12px',
              borderRadius: 8,
              background: '#FFFFFF',
              border: '1px solid #3497D3',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}>
              <Text style={{ fontSize: 12, fontWeight: 700, color: '#3497D3', textAlign: 'center', margin: 0 }}>
                Policies
              </Text>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', padding: '6px 8px', borderRadius: 6, background: '#FFFFFF', border: '1px solid #3497D3' }}>
                  <Text style={{ fontSize: 10, fontWeight: 600, color: '#3497D3', margin: 0 }}>Total</Text>
                  <Text style={{ fontSize: 14, fontWeight: 800, color: '#3497D3', margin: 0 }}>{data?.all_policies || 0}</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', padding: '6px 8px', borderRadius: 6, background: '#49B870' }}>
                  <Text style={{ fontSize: 10, fontWeight: 600, color: '#FFFFFF', margin: 0 }}>Active</Text>
                  <Text style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', margin: 0 }}>{data?.active_policies || 0}</Text>
                </div>
              </div>
            </div>

            {/* Enrollees Container */}
            <div style={{
              padding: '12px',
              borderRadius: 8,
              background: '#FFFFFF',
              border: '1px solid #49B870',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}>
              <Text style={{ fontSize: 12, fontWeight: 700, color: '#49B870', textAlign: 'center', margin: 0 }}>
                Enrollees
              </Text>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', padding: '6px 8px', borderRadius: 6, background: '#FFFFFF', border: '1px solid #49B870' }}>
                  <Text style={{ fontSize: 10, fontWeight: 600, color: '#49B870', margin: 0 }}>Total</Text>
                  <Text style={{ fontSize: 14, fontWeight: 800, color: '#49B870', margin: 0 }}>{data?.all_enrollees || 0}</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', padding: '6px 8px', borderRadius: 6, background: '#3497D3' }}>
                  <Text style={{ fontSize: 10, fontWeight: 600, color: '#FFFFFF', margin: 0 }}>Active</Text>
                  <Text style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', margin: 0 }}>{data?.active_enrollees || 0}</Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
      {/* No action available to this role — render no hover toolbar at all,
          rather than an empty panel with a dead "More" button. */}
      {orgActions.canAdministerOrg ? (
      <div
        ref={actionsRef}
        className="action-buttons"
        onMouseEnter={() => {
          try {
            if (actionsRef && actionsRef.current) {
              actionsRef.current.style.opacity = '1';
            }
          } catch { }
        }}
        onMouseLeave={(e) => {
          try {
            const next = e.relatedTarget;
            // If leaving to somewhere inside the card, keep visible
            if (cardRef && cardRef.current && next && cardRef.current.contains(next)) return;
            if (actionsRef && actionsRef.current) {
              actionsRef.current.style.opacity = '0';
            }
          } catch { }
        }}
        style={{
          position: 'absolute',
          bottom: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 6,
          zIndex: 2,
          padding: 8,
          background: 'rgba(255,255,255,0.98)',
          border: '1px solid #3497D3',
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          backdropFilter: 'saturate(180%) blur(8px)',
          opacity: 0,
          transition: 'opacity .2s ease'
        }}
      >
        {/* Primary actions — each gated by the endpoint behind it. */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {orgActions.canEdit ? (
          <Tooltip title="Edit Organization" placement="top">
            <Button
              type="text"
              shape="circle"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); editHandler(data); }}
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #3497D3',
                color: '#3497D3',
                width: 32,
                height: 32,
                transition: 'all .2s ease'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.backgroundColor = '#3497D3'; 
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.backgroundColor = '#f8fafc'; 
                e.currentTarget.style.color = '#3497D3';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          </Tooltip>
          ) : null}

          {orgActions.canManagePlans ? (
          <Tooltip title="Manage Plans" placement="top">
            <Button
              type="text"
              shape="circle"
              icon={<SettingOutlined />}
              onClick={(e) => { e.stopPropagation(); manageHandle(data); }}
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #49B870',
                color: '#49B870',
                width: 32,
                height: 32,
                transition: 'all .2s ease'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.backgroundColor = '#49B870'; 
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.backgroundColor = '#f8fafc'; 
                e.currentTarget.style.color = '#49B870';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          </Tooltip>
          ) : null}
        </div>

        {/* Divider */}
        <div style={{ 
          width: 1, 
          height: 24, 
          background: 'linear-gradient(to bottom, transparent, #3497D3, transparent)',
          margin: '0 4px'
        }} />

        {/* Secondary Actions - Dropdown */}
        <Dropdown
          menu={{
            items: [
              ...(orgActions.canRenew ? [{
                key: 'renew',
                label: 'Renew Policy',
                icon: <CalendarOutlined />,
                onClick: () => {
                  if (renewPolicyRef.current && typeof renewPolicyRef.current.open === 'function') {
                    renewPolicyRef.current.open();
                  }
                }
              }] : []),
              ...(orgActions.canSetStatus ? [{
                key: 'status',
                label: enabled ? 'Disable Organization' : 'Enable Organization',
                icon: enabled ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                onClick: () => { statusHandler(data); }
              }] : []),
              ...(orgActions.canSetPassword ? [{
                key: 'password',
                label: 'Set Password',
                icon: <LockOutlined />,
                onClick: () => { setVisiblePassword(true); }
              }] : [])
            ]
          }}
          placement="topCenter"
          trigger={['click']}
        >
          <Tooltip title="More Actions" placement="top">
            <Button
              type="text"
              shape="circle"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #64748b',
                color: '#64748b',
                width: 32,
                height: 32,
                transition: 'all .2s ease'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.backgroundColor = '#64748b'; 
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.backgroundColor = '#f8fafc'; 
                e.currentTarget.style.color = '#64748b';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          </Tooltip>
        </Dropdown>

        {/* Renew Policy Component - Hidden but accessible via dropdown */}
        <div className="renew-policy-hidden" style={{ display: 'none' }}>
          <RenewOrgPolicy ref={renewPolicyRef} data={data} action={renewHandler} viewType="tile" />
        </div>
      </div>
      ) : null}

      {/* Password Management Modal */}
      <Modal
        title={`Set Password - ${data?.org_name || 'Organization'}`}
        open={visiblePassword}
        onCancel={() => setVisiblePassword(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <PasswordManagementModal 
          organization={data} 
          onClose={() => setVisiblePassword(false)}
        />
      </Modal>
    </div>
  );
};

export default Organizations;